// When a patient has no explicitly preferred partner logo, we fall back to a
// hierarchy rather than blindly taking the first partner in the list. Order
// matters here: superclinical wins over walmart per product team decision,
// based on the fact that Walmart has no workflow implications.
const FALLBACK_PARTNER_LOGO_PRIORITY = ["superclinical"] as const;
// Walmart excluded from fallback
const FALLBACK_EXCLUDED_PARTNER_UIDS: readonly string[] = ["walmart"];
/**
 * Selects which partner logo to show when a patient has no preferred partner
 * logo.
 *
 * This is distinct from the preferredPartnerUid path because that path is powered
 * by the partner service and is dictated based on partner-service configurations.
 * The EMR has historically hijacked partner affiliations in some cases to provide
 * for specialized experiences based on things that are not controlled in the
 * partner service. In this case, `superclinical` is provided as partner uid from
 * the EMR backend but without partner-service having any affiliation itself. With
 * that being the case this path is provided for the EMR to apply its ad-hoc decoupled
 * prioritization rules without attempting to pollute it back upstream to partner.
 *
 * TODO: https://linear.app/curai/issue/FIDO-417
 * If we want this to be centralized into the partner service wie will need to approach
 * it holistically and stop injecting phantom affiliations into EMR request/responses
 * based on EMR business rules.
 */

export const selectFallbackPartnerUid = (
  partnerUids?: string[] | null,
): string | undefined => {
  if (!partnerUids?.length) return undefined;
  const candidates = partnerUids.filter(
    (uid) => !FALLBACK_EXCLUDED_PARTNER_UIDS.includes(uid),
  );
  const prioritized = FALLBACK_PARTNER_LOGO_PRIORITY.find((uid) =>
    candidates.includes(uid),
  );
  return prioritized ?? candidates[0];
};
